import { getFilteredMountingBrackets } from '@product/ProductTabs/tabs/ProductSelectionTab/getFilteredMountingBrackets';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { AssociationType } from '@libs/associations';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { getStandardMountingVariant, isNumGliederEven } from '@libs/echain';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getLocale } from '@libs/constants';

export const useMountingBracketsSelection = () => {
  const router = useRouter();
  const locale = getLocale(router);
  const isEdit = useEchainProductStore(state => state.isEdit);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const addEchainPartsListItem = useEchainProductStore(state => state.addEchainPartsListItem);
  const eChainPartsList = useEchainProductStore(state => state.eChainPartsList);
  const setSelectedMountingBracketGroup = useEchainProductStore(state => state.setSelectedMountingBracketGroup);
  const setSelectedDistinctVariant = useEchainProductStore(state => state.setSelectedDistinctVariant);
  const setSelectedMountingVariant = useEchainProductStore(state => state.setSelectedMountingVariant);
  const setGroupedMountingBracketsForCurrentProduct = useEchainProductStore(
    state => state.setGroupedMountingBracketsForCurrentProduct
  );
  const groupedMountingBrackets = useEchainProductStore(state => state.groupedMountingBrackets);
  const selectedMountingBracketGroup = useEchainProductStore(state => state.selectedMountingBracketGroup);
  const selectedDistinctVariant = useEchainProductStore(state => state.selectedDistinctVariant);
  const selectedMountingVariant = useEchainProductStore(state => state.selectedMountingVariant);
  const kette = useEchainProductStore(state => state.kette);
  const hasUserDeselectedMountingBrackets = useEchainProductStore(state => state.hasUserDeselectedMountingBrackets);
  const [hasMountingBracketSelectionsChanged, setHasMountingBracketSelectionsChanged] = useState(false);

  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  useEffect(() => {
    if (isLoadingUniqueAssociatedProducts) {
      return;
    }

    setGroupedMountingBracketsForCurrentProduct(
      uniqueAssociatedProducts
        ? getFilteredMountingBrackets(
            currentProduct,
            uniqueAssociatedProducts,
            getAttributeValue<string>(
              currentProduct?.attributes?.sorting_mounting_bracket_group as ProductAttribute<string>
            ),
            isNumGliederEven(kette.NumGlieder)
          )
        : []
    );
  }, [
    kette,
    currentProduct,
    isLoadingUniqueAssociatedProducts,
    uniqueAssociatedProducts,
    setGroupedMountingBracketsForCurrentProduct,
  ]);

  useEffect(() => {
    const isMountingBracketSelected =
      selectedMountingBracketGroup && selectedDistinctVariant && selectedMountingVariant;

    if (groupedMountingBrackets?.length && isMountingBracketSelected) {
      const foundGroupedMountingBracketFromSameType = groupedMountingBrackets?.find(
        groupedMountingBracket => groupedMountingBracket.key === selectedMountingBracketGroup.key
      );
      const foundDistinctVariantsFromSameType = foundGroupedMountingBracketFromSameType?.distinctVariants?.find(
        distinctVariant => distinctVariant.key === selectedDistinctVariant.key
      );

      // when there are no real mounting variants, we must take the defaultVariant of the distinct variant into consideration, otherwise the expected behaviour will break for this case
      const foundMountingVariantsFromSameType = (
        foundDistinctVariantsFromSameType?.mountingVariants || [foundDistinctVariantsFromSameType?.defaultVariant]
      ).find(
        mountingVariant =>
          getAttributeValue<string>(mountingVariant?.attributes?.mounting_variant as ProductAttribute<string>) ===
          getAttributeValue<string>(selectedMountingVariant.attributes?.mounting_variant as ProductAttribute<string>)
      );

      if (foundMountingVariantsFromSameType && foundMountingVariantsFromSameType.id !== selectedMountingVariant.id) {
        setSelectedMountingBracketGroup(foundGroupedMountingBracketFromSameType);
        setSelectedDistinctVariant(foundDistinctVariantsFromSameType);
        setSelectedMountingVariant(foundMountingVariantsFromSameType);
        return;
      }

      const isSelectedMountingBracketAvailable = Boolean(foundMountingVariantsFromSameType);
      if (!isSelectedMountingBracketAvailable) {
        setSelectedMountingBracketGroup(groupedMountingBrackets[0]);
        setSelectedDistinctVariant(groupedMountingBrackets[0].distinctVariants[0]);
        setSelectedMountingVariant(getStandardMountingVariant(groupedMountingBrackets[0].distinctVariants[0]));

        setHasMountingBracketSelectionsChanged(true);
      }
    }

    if (
      groupedMountingBrackets?.[0]?.distinctVariants &&
      !isEdit &&
      !isMountingBracketSelected &&
      !hasUserDeselectedMountingBrackets
    ) {
      setSelectedMountingBracketGroup(groupedMountingBrackets[0]);
      setSelectedDistinctVariant(groupedMountingBrackets[0].distinctVariants[0]);
      setSelectedMountingVariant(getStandardMountingVariant(groupedMountingBrackets[0].distinctVariants[0]));
    }
  }, [
    isEdit,
    groupedMountingBrackets,
    setSelectedMountingBracketGroup,
    setSelectedDistinctVariant,
    setSelectedMountingVariant,
    selectedMountingBracketGroup,
    selectedDistinctVariant,
    selectedMountingVariant,
    hasUserDeselectedMountingBrackets,
  ]);

  useEffect(() => {
    if (
      currentProduct &&
      selectedMountingVariant?.id &&
      !eChainPartsList?.partsList.some(part => part.id === selectedMountingVariant.id)
    ) {
      void addEchainPartsListItem(selectedMountingVariant, locale, AssociationType.MOUNTING_BRACKETS);
    }
  }, [selectedMountingVariant, addEchainPartsListItem, eChainPartsList, currentProduct, locale]);

  return {
    hasMountingBracketSelectionsChanged,
    resetHasMountingBracketSelectionsChanged: () => setHasMountingBracketSelectionsChanged(false),
  };
};
